import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { ReferenceImageItem } from '../api/referenceImages'
import type { ReferenceImageUrlHints } from '../lib/referenceImageUrl'
import { resolveReferenceImageDisplayUrl } from '../lib/referenceImageUrl'
import {
  defaultRefreshUrlExecutor,
  refreshReferenceUrlOnce,
  type RefreshUrlExecutor,
} from '../lib/referenceUrlRefresh'

/** 同一个「地址」最多自动补签几次（之后仍失败才向用户报错） */
const MAX_AUTO_HEALS = 2

export type ImageUrlSelfHealOptions = {
  /** 原始地址（列表/父组件传入，可能是过期或无效的签名链） */
  rawUrl: string | null | undefined
  /** 后端 objectKey；有值时才启用「自动补签」，无值退化为普通重新加载 */
  objectKey?: string
  /** URL 解析 hints（bucket/objectKey） */
  hints?: ReferenceImageUrlHints
  /**
   * 把原始 URL 转成最终展示 URL（追加七牛缩略参数等）。
   * 默认 {@link resolveReferenceImageDisplayUrl}。
   */
  transformUrl?: (rawUrl: string, hints?: ReferenceImageUrlHints) => string
  /** 自定义补签执行器；默认直接调后端 refresh-url */
  executor?: RefreshUrlExecutor
  /** 补签成功回调，供宿主把新 url 回写到列表状态 */
  onRefreshed?: (item: ReferenceImageItem) => void
}

export type ImageUrlSelfHealState = {
  /** 当前应渲染的地址（补签成功后为新地址） */
  displayUrl: string
  /** 最终不可用（已尝试补签，不会再自动重试） */
  failed: boolean
  /** 正在补签（可展示「正在恢复」而非直接报错） */
  healing: boolean
  /** 变化即触发 <img key> 重挂载，用于重试 */
  reloadTick: number
  /** img onError 统一入口：先自愈，失败才 failed */
  handleError: () => void
  /** img onLoad：清除失败态 */
  handleLoad: () => void
  /** 用户手动重试：重置计数并重新补签（或直接重载） */
  retry: () => void
}

/**
 * 图片地址自愈：
 *
 * 用户点开就是想看图，「URL 过期/签名失效」不该直接甩一句错误。
 * 因此加载失败时先向后端要一份新签名再试，只有补签也救不回来（或没有 objectKey）
 * 才把 failed 置 true，让调用方渲染失败占位。
 *
 * 并发/去重由 {@link refreshReferenceUrlOnce} 统一兜底，整屏裂图也不会打爆后端。
 */
export function useImageUrlSelfHeal(options: ImageUrlSelfHealOptions): ImageUrlSelfHealState {
  const { rawUrl, objectKey, hints, transformUrl, executor, onRefreshed } = options

  const [overrideUrl, setOverrideUrl] = useState<string | null>(null)
  const [reloadTick, setReloadTick] = useState(0)
  const [failed, setFailed] = useState(false)
  const [healing, setHealing] = useState(false)

  /** 自动补签次数 + 上次尝试的「地址指纹」，防止同一地址反复补签形成循环 */
  const healCountRef = useRef(0)
  const healTokenRef = useRef<string | null>(null)

  /** hints/transformUrl/onRefreshed 多为每次渲染新建的对象，用 ref 读取避免无谓重渲染与重连依赖 */
  const latestRef = useRef({ hints, transformUrl, executor, onRefreshed, objectKey })
  latestRef.current = { hints, transformUrl, executor, onRefreshed, objectKey }

  const hintsKey = `${hints?.bucket ?? ''}\u0001${hints?.objectKey ?? ''}`

  const baseUrl = useMemo(() => {
    const raw = (rawUrl ?? '').trim()
    if (!raw) return ''
    const { transformUrl: transform, hints: h } = latestRef.current
    if (transform) return transform(raw, h)
    return resolveReferenceImageDisplayUrl(raw, h)
    // hintsKey 变化等价于 hints 语义变化
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawUrl, hintsKey])

  const displayUrl = overrideUrl && overrideUrl.trim() ? overrideUrl : baseUrl

  useEffect(() => {
    setOverrideUrl(null)
    setReloadTick(0)
    setFailed(false)
    setHealing(false)
    healCountRef.current = 0
    healTokenRef.current = null
  }, [rawUrl, objectKey])

  const applyRefreshed = useCallback((next: ReferenceImageItem): boolean => {
    const { transformUrl: transform, hints: h, onRefreshed: notify } = latestRef.current
    if (!next?.url) return false
    const nextUrl = transform
      ? transform(next.url, h)
      : resolveReferenceImageDisplayUrl(next.url, h)
    if (!nextUrl) return false
    setOverrideUrl(nextUrl)
    setReloadTick((t) => t + 1)
    setFailed(false)
    notify?.(next)
    return true
  }, [])

  const handleError = useCallback(() => {
    const { objectKey: key, executor: customExecutor, transformUrl: transform, hints: h } =
      latestRef.current
    const trimmedKey = (key ?? '').trim()
    const currentUrl = overrideUrl && overrideUrl.trim() ? overrideUrl : baseUrl

    const canHeal =
      Boolean(trimmedKey) &&
      healCountRef.current < MAX_AUTO_HEALS &&
      healTokenRef.current !== `${trimmedKey}|${currentUrl}`

    if (!canHeal) {
      setHealing(false)
      setFailed(true)
      return
    }

    healTokenRef.current = `${trimmedKey}|${currentUrl}`
    healCountRef.current += 1
    setHealing(true)
    void refreshReferenceUrlOnce(trimmedKey, customExecutor ?? defaultRefreshUrlExecutor, {
      force: true,
    })
      .then((next) => {
        const nextUrl = next?.url
          ? transform
            ? transform(next.url, h)
            : resolveReferenceImageDisplayUrl(next.url, h)
          : ''
        // 补签拿回的地址和刚才完全一样 → 说明补签救不回来
        // （典型情况：AK/SK 不配对，重签出来照样被云端拒绝；或对象已被删除）
        if (!next || !nextUrl || nextUrl === currentUrl) {
          setFailed(true)
          return
        }
        applyRefreshed(next)
      })
      .catch(() => {
        setFailed(true)
      })
      .finally(() => {
        setHealing(false)
      })
  }, [applyRefreshed, baseUrl, overrideUrl])

  const handleLoad = useCallback(() => {
    setHealing(false)
    setFailed(false)
  }, [])

  const retry = useCallback(() => {
    const { objectKey: key, executor: customExecutor } = latestRef.current
    const trimmedKey = (key ?? '').trim()
    setFailed(false)
    setHealing(false)
    if (!trimmedKey) {
      setReloadTick((t) => t + 1)
      return
    }
    healCountRef.current = 0
    healTokenRef.current = null
    setHealing(true)
    void refreshReferenceUrlOnce(trimmedKey, customExecutor ?? defaultRefreshUrlExecutor, {
      force: true,
    })
      .then((next) => {
        if (next?.url) applyRefreshed(next)
        // 手动重试即使新地址与旧地址相同也要强制重挂一次
        setReloadTick((t) => t + 1)
      })
      .catch(() => {
        setReloadTick((t) => t + 1)
      })
      .finally(() => {
        setHealing(false)
      })
  }, [applyRefreshed])

  return { displayUrl, failed, healing, reloadTick, handleError, handleLoad, retry }
}
